import { useEffect } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useDeptCreate, useDeptUpdate } from "services";
import { useOrgChartHook } from "stores/OrgChart";
import { i18n } from "translations";
import * as Yup from "yup";

const DepartmentFormSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("nameIsRequired")),
  code: Yup.string().required(i18n.t("codeIsRequired")),
  deptType: Yup.string().required(i18n.t("typeIsRequired")),
  costCenterCode: Yup.string(),
  deptCode: Yup.string(),
  branch: Yup.object({
    label: Yup.string()?.notRequired(),
    value: Yup.string()?.notRequired(),
  })?.notRequired(),
});

type DepartmentForm = Yup.InferType<typeof DepartmentFormSchema>;

export const useDepartmentDialog = ({
  onToggle,
  isEdit,
}: {
  isEdit: boolean;
  onToggle: () => void;
}) => {
  const methods = useForm<DepartmentForm>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(DepartmentFormSchema),
  });
  const { nodeSelected, addNode, updateNode } = useOrgChartHook();
  const { mutate: createDept, isLoading } = useDeptCreate({
    queryKey: "deptCreateHook",
    options: {
      onSuccess: (res) => {
        addNode(Number(nodeSelected?.id ?? 0), {
          ...res.data,
          children: [],
        });
        onToggle();
      },
    },
  });
  const { mutate: updateDept } = useDeptUpdate({
    queryKey: "deptUpdateHook",
    options: {
      onSuccess: (res) => {
        updateNode({ ...res.data });
        onToggle();
      },
    },
  });

  useEffect(() => {
    if (isEdit) {
      methods.setValue("name", nodeSelected?.name ?? "");
      methods.setValue("code", nodeSelected?.code ?? "");
      methods.setValue("deptType", nodeSelected?.deptType ?? "");
      methods.setValue("costCenterCode", nodeSelected?.costCenterCode ?? "");
      methods.setValue("deptCode", nodeSelected?.deptCode ?? "");
      if (nodeSelected?.branch) {
        methods.setValue("branch", {
          label: String(nodeSelected?.branch?.name),
          value: String(nodeSelected?.branch?.id),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const handleSubmit = (data: DepartmentForm) => {
    if (isEdit) {
      updateDept({
        id: Number(nodeSelected?.id ?? 0),
        name: data.name,
        code: data.code,
        deptType: data.deptType,
        branchId: Number(data?.branch?.value) || undefined,
        costCenterCode: data.costCenterCode,
        deptCode: data.deptCode,
      });
    } else {
      createDept({
        name: data.name,
        code: data.code,
        deptType: data.deptType,
        parentId: nodeSelected?.id ? nodeSelected.id : null,
        branchId: Number(data?.branch?.value) || undefined,
        costCenterCode: data.costCenterCode,
        deptCode: data.deptCode,
      });
    }
  };

  return {
    nodeSelected,
    loading: isLoading,
    methods,
    handleSubmit,
  };
};
